import RetryContext from 'utils/retry/RetryContext';
import RetryPolicy from 'utils/retry/RetryPolicy';
import TimeoutError from 'utils/retry/TimeoutError';
import { Duration } from '@instinet-analytics/core-lib';

export interface ExponentialRetryPolicyOptions {
    // Retry timeout per call in ms
    retryTimeout: number;
    // Total timeout in ms
    totalTimeout: number;
    // If set then retry will continue beyond the totalTimeout at this interval (ms)
    postTimeoutInterval: number;
    // binary exponential factor
    base: number;
    /*
     * True to retry on errors other than a Timeout
     * (by default this policy halts the retry loop on an unexpected exception other than a timeout)
     */
    retryOnError: boolean;
    retryLog?: (durationMs: number) => void;
}

export default class ExponentialRetryPolicy implements RetryPolicy {
    public retryTimeout: number;
    public totalTimeout: number;
    public postTimeoutInterval: number | undefined;
    public readonly retryLog?: (durationMs: number) => void;
    private readonly base: number;
    private readonly retryOnError: boolean;

    public constructor(options?: Partial<ExponentialRetryPolicyOptions>) {
        this.retryTimeout = options?.retryTimeout || 5 * 1000; // 5 sec
        this.totalTimeout = options?.totalTimeout || 60 * 1000; // 60 sec
        this.postTimeoutInterval = options?.postTimeoutInterval;
        this.base = options?.base || 2;
        this.retryOnError = !!options?.retryOnError;
        this.retryLog = options?.retryLog;
    }

    public backOff(retryContext: RetryContext): Duration {
        if (
            retryContext.cumulativeTime > this.totalTimeout &&
            this.postTimeoutInterval !== undefined
        ) {
            return this.postTimeoutInterval;
        }
        // take the min of next computed retry time
        // and totalTimeout minus cumulativeTime ie current retry time
        // But never wait longer than postTimeoutInterval, if it is supplied
        return Math.min(
            Math.pow(this.base, retryContext.count + 1) * 1000,
            this.totalTimeout - retryContext.cumulativeTime,
            this.postTimeoutInterval ? this.postTimeoutInterval : Infinity
        );
    }

    public shouldRetry(retryContext: RetryContext): boolean {
        if (this.postTimeoutInterval !== undefined) {
            return true;
        }
        if (retryContext.error) {
            if (
                retryContext.error.constructor !== TimeoutError &&
                !this.retryOnError
            ) {
                return false;
            }
        }

        return retryContext.cumulativeTime < this.totalTimeout;
    }
}
